import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Star,
  MapPin,
  Users,
  Bed,
  Bath,
  Wifi,
  Car,
  Coffee,
  Waves,
  Dumbbell,
  Sparkles,
  Utensils,
  Home,
  Building,
  Crown,
  Building2
} from "lucide-react";
import SaudiRiyal from "@/components/shared/saudi-riyal";
import { roomService } from "@/services/roomService";
import { propertyTypesService, PropertyType as ApiPropertyType } from "@/services/propertyTypesService";
import { propertiesService, Property as ApiProperty, PropertySearchFilters } from "@/services/propertiesService";
import { Property as MockProperty, PropertyType as MockPropertyType } from "@/mockData/rooms";
import { PropertyForm } from "@/components/rooms/property-form";
import { PropertyTypeForm } from "@/components/rooms/property-type-form";
import { PropertyTypeView } from "@/components/rooms/property-type-view";
import { PropertyView } from "@/components/rooms/property-view";
import { PropertyMediaManager } from "@/components/rooms/property-media-manager";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

export default function Rooms() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("properties");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [loading, setLoading] = useState(false);
  
  // Properties filters state
  const [propertiesFilters, setPropertiesFilters] = useState<PropertySearchFilters>({
    page: 1,
    limit: 10,
    sort_by: 'createdAt',
    sort_order: 'DESC'
  });
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState<ApiProperty | null>(null);
  const [showPropertyView, setShowPropertyView] = useState(false);
  const [viewingProperty, setViewingProperty] = useState<ApiProperty | null>(null);
  const [showPropertyTypeForm, setShowPropertyTypeForm] = useState(false);
  const [editingPropertyType, setEditingPropertyType] = useState<ApiPropertyType | null>(null);
  const [showPropertyTypeView, setShowPropertyTypeView] = useState(false);
  const [viewingPropertyType, setViewingPropertyType] = useState<ApiPropertyType | null>(null);
  const [deletePropertyType, setDeletePropertyType] = useState<ApiPropertyType | null>(null);
  const [deleteProperty, setDeleteProperty] = useState<ApiProperty | null>(null);
  const [selectedPropertyForMedia, setSelectedPropertyForMedia] = useState<string>("");
  const { toast } = useToast();

  // React Query for property types data (using real API)
  const { data: propertyTypesResponse, isLoading: typesLoading, refetch: refetchTypes } = useQuery({
    queryKey: ['propertyTypes'],
    queryFn: () => {
      console.log('🔧 React Query calling propertyTypesService.getPropertyTypes');
      return propertyTypesService.getPropertyTypes({ page: 1, pageSize: 100 });
    },
    staleTime: 30000, // 30 seconds
  });

  // React Query for properties data (using real API)
  const { data: propertiesResponse, isLoading: propertiesLoading, refetch: refetchProperties } = useQuery({
    queryKey: ['properties', propertiesFilters],
    queryFn: () => {
      console.log('🔧 React Query calling propertiesService.getProperties');
      return propertiesService.getProperties(propertiesFilters);
    },
    staleTime: 30000, // 30 seconds
  });

  // Extract property types from API response with safety checks
  // The API returns data directly as an array, but the service wraps it in propertyTypes
  const propertyTypes = propertyTypesResponse?.data?.propertyTypes || propertyTypesResponse?.data || [];
  
  // Extract properties from API response with safety checks
  const properties = propertiesResponse?.data?.properties || [];
  const propertiesPagination = propertiesResponse?.data?.pagination || {
    current_page: 1,
    total_pages: 0,
    total_items: 0,
    per_page: 10
  };
  
  // Debug logging with safety checks
  console.log('🔧 Rooms.tsx state:', {
    propertyTypesResponse,
    propertyTypes: Array.isArray(propertyTypes) ? propertyTypes : [],
    propertyTypesCount: Array.isArray(propertyTypes) ? propertyTypes.length : 0,
    typesLoading,
    propertiesResponse,
    properties: Array.isArray(properties) ? properties : [],
    propertiesCount: Array.isArray(properties) ? properties.length : 0,
    propertiesLoading,
    propertiesPagination
  });

  // Properties are now loaded via React Query above

  const getPropertyTypeName = (typeId: string) => {
    if (!Array.isArray(propertyTypes) || !typeId) return "Unknown";
    const type = propertyTypes.find(t => t && t.id && t.id.toString() === typeId);
    return type ? type.name_en : "Unknown";
  };

  const getPropertyTypeIcon = (typeId: string) => {
    if (!Array.isArray(propertyTypes) || !typeId) return "🏠";
    const type = propertyTypes.find(t => t && t.id && t.id.toString() === typeId);
    return type ? type.icon : "🏠";
  };

  // Mutations for property type operations
  const createPropertyTypeMutation = useMutation({
    mutationFn: (data: any) => propertyTypesService.createPropertyType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propertyTypes'] });
      setShowPropertyTypeForm(false);
      setEditingPropertyType(null);
      toast({
        title: t("success"),
        description: t("propertyTypeCreated") || "Property type created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: t("error"),
        description: t("failedToCreatePropertyType") || "Failed to create property type",
        variant: "destructive",
      });
    },
  });

  const updatePropertyTypeMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => {
      console.log('🔧 Mutation called with:', { id, data });
      return propertyTypesService.updatePropertyType(id, data);
    },
    onSuccess: (response, variables) => {
      console.log('🔧 Update successful:', { response, variables });
      queryClient.invalidateQueries({ queryKey: ['propertyTypes'] });
      setShowPropertyTypeForm(false);
      setEditingPropertyType(null);
      toast({
        title: t("success"),
        description: t("propertyTypeUpdated") || "Property type updated successfully",
      });
    },
    onError: (error, variables) => {
      console.error('🔧 Update failed:', { error, variables });
      toast({
        title: t("error"),
        description: t("failedToUpdatePropertyType") || "Failed to update property type",
        variant: "destructive",
      });
    },
  });

  const deletePropertyTypeMutation = useMutation({
    mutationFn: (id: number) => propertyTypesService.deletePropertyType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propertyTypes'] });
      setDeletePropertyType(null);
      toast({
        title: t("success"),
        description: t("propertyTypeDeleted") || "Property type deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: t("error"),
        description: t("failedToDeletePropertyType") || "Failed to delete property type",
        variant: "destructive",
      });
    },
  });

  // Mutations for property operations
  const createPropertyMutation = useMutation({
    mutationFn: ({ data, mediaFiles }: { data: any; mediaFiles?: File[] }) => 
      propertiesService.createProperty(data, mediaFiles),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      setShowPropertyForm(false);
      setEditingProperty(null);
      toast({
        title: t("success"),
        description: t("propertyCreated") || "Property created successfully",
      });
    },
    onError: (error: any) => {
      console.error('🔧 Create property mutation error:', error);
      
      // Extract detailed error message
      let errorMessage = t("failedToCreateProperty") || "Failed to create property";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: t("error"),
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const updatePropertyMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      propertiesService.updateProperty(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      setShowPropertyForm(false);
      setEditingProperty(null);
      toast({
        title: t("success"),
        description: t("propertyUpdated") || "Property updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: t("error"),
        description: t("failedToUpdateProperty") || "Failed to update property",
        variant: "destructive",
      });
    },
  });

  const deletePropertyMutation = useMutation({
    mutationFn: (id: number) => propertiesService.deleteProperty(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      setDeleteProperty(null);
      toast({
        title: t("success"),
        description: t("propertyDeleted") || "Property deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: t("error"),
        description: t("failedToDeleteProperty") || "Failed to delete property",
        variant: "destructive",
      });
    },
  });

  const getAmenityIcon = (amenity: string) => {
    const iconMap: Record<string, any> = {
      wifi: Wifi,
      parking: Car,
      kitchen: Coffee,
      airConditioning: Sparkles,
      swimmingPool: Waves,
      gym: Dumbbell,
      spa: Sparkles,
      restaurant: Utensils,
      roomService: Coffee,
      laundry: Coffee,
      balcony: Home,
      seaView: Waves,
      cityView: Building
    };
    return iconMap[amenity] || Coffee;
  };

  const filteredProperties = properties.filter(property => {
    const matchesSearch = property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === "all" || property.type_id === selectedType;
    return matchesSearch && matchesType;
  });

  const handleCreateProperty = () => {
    setEditingProperty(null);
    setShowPropertyForm(true);
  };

  const handleViewProperty = (property: ApiProperty) => {
    setViewingProperty(property);
    setShowPropertyView(true);
  };

  const handleEditProperty = (property: ApiProperty) => {
    setEditingProperty(property);
    setShowPropertyForm(true);
  };

  const handleSaveProperty = (property: any, mediaFiles?: File[]) => {
    console.log('🔧 handleSaveProperty called:', {
      isUpdate: !!editingProperty,
      property,
      mediaFiles: mediaFiles?.length || 0,
      editingPropertyId: editingProperty?.id
    });

    if (editingProperty) {
      // Update existing property - handle both string and number IDs
      const propertyId = typeof editingProperty.id === 'string' ? 
        parseInt(editingProperty.id) : editingProperty.id;
      
      updatePropertyMutation.mutate({ 
        id: propertyId, 
        data: property 
      });
    } else {
      // Create new property with media files
      createPropertyMutation.mutate({ 
        data: property, 
        mediaFiles 
      });
    }
  };

  const handleClosePropertyForm = () => {
    setShowPropertyForm(false);
    setEditingProperty(null);
  };

  const handleClosePropertyView = () => {
    setShowPropertyView(false);
    setViewingProperty(null);
  };

  // Property Type handlers
  const handleCreatePropertyType = () => {
    setEditingPropertyType(null);
    setShowPropertyTypeForm(true);
  };

  const handleEditPropertyType = (propertyType: ApiPropertyType) => {
    setEditingPropertyType(propertyType);
    setShowPropertyTypeForm(true);
  };

  const handleViewPropertyType = (propertyType: ApiPropertyType) => {
    setViewingPropertyType(propertyType);
    setShowPropertyTypeView(true);
  };

  const handleSavePropertyType = (propertyType: any) => {
    // Convert the form data to API format
    const apiData = {
      name_en: propertyType.name_en,
      name_ar: propertyType.name_ar,
      description_en: propertyType.description_en,
      description_ar: propertyType.description_ar,
      icon: propertyType.icon
    };

    if (editingPropertyType) {
      // Update existing property type - handle both string and number IDs
      const typeId = typeof editingPropertyType.id === 'string' ? 
        parseInt(editingPropertyType.id) : editingPropertyType.id;
      
      console.log('🔧 Updating property type:', {
        originalId: editingPropertyType.id,
        convertedId: typeId,
        data: apiData
      });
      
      updatePropertyTypeMutation.mutate({ 
        id: typeId, 
        data: apiData 
      });
    } else {
      // Create new property type
      console.log('🔧 Creating property type:', apiData);
      createPropertyTypeMutation.mutate(apiData);
    }
  };

  const handleClosePropertyTypeForm = () => {
    setShowPropertyTypeForm(false);
    setEditingPropertyType(null);
  };

  const handleClosePropertyTypeView = () => {
    setShowPropertyTypeView(false);
    setViewingPropertyType(null);
  };

  const handleDeletePropertyType = async (propertyType: ApiPropertyType) => {
    try {
      deletePropertyTypeMutation.mutate(parseInt(propertyType.id));
    } catch (error) {
      console.error("Error deleting property type:", error);
      toast({
        title: t("error"),
        description: t("failedToDeletePropertyType"),
        variant: "destructive",
      });
    }
    setDeletePropertyType(null);
  };

  const handleDeleteProperty = async (property: ApiProperty) => {
    try {
      const propertyId = typeof property.id === 'string' ? 
        parseInt(property.id) : property.id;
      
      console.log('🔧 handleDeleteProperty called:', {
        property,
        propertyId
      });
      
      deletePropertyMutation.mutate(propertyId);
    } catch (error) {
      console.error("Error deleting property:", error);
      toast({
        title: t("error"),
        description: t("failedToDeleteProperty"),
        variant: "destructive",
      });
    }
    setDeleteProperty(null);
  };

  // Early return with error handling
  if (!Array.isArray(propertyTypes) && !typesLoading) {
    return (
      <div className="space-y-6">
        <Card className="p-6">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-red-600">Error Loading Data</h2>
            <p className="text-muted-foreground mt-2">
              There was an error loading the property types. Please refresh the page.
            </p>
            <Button onClick={() => window.location.reload()} className="mt-4">
              Reload Page
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">{t("roomManagement")}</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            {t("managePropertyInventory")}
          </p>
        </div>
        <Button onClick={handleCreateProperty} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          {t("createProperty")}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="properties">{t("properties")}</TabsTrigger>
          <TabsTrigger value="property-types">{t("propertyTypes")}</TabsTrigger>
          <TabsTrigger value="media">{t("mediaManagement")}</TabsTrigger>
        </TabsList>

        <TabsContent value="properties" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                {t("filter")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder={t("search")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={t("propertyType")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("all")}</SelectItem>
                    {propertyTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.icon} {type.name_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Properties Table */}
          <Card>
            <CardHeader>
              <CardTitle>{t("rooms")}</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("name")}</TableHead>
                    <TableHead>{t("propertyType")}</TableHead>
                    <TableHead>{t("location")}</TableHead>
                    <TableHead>{t("capacity")}</TableHead>
                    <TableHead>{t("price")}</TableHead>
                    <TableHead>{t("status")}</TableHead>
                    <TableHead>{t("actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProperties.map((property) => (
                    <TableRow key={property.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            {property.images && property.images.length > 0 ? (
                              <img 
                                src={property.images[0].url} 
                                alt={property.images[0].alt_text || property.name}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ) : (
                              <Building2 className="h-6 w-6 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium">{property.name}</div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {property.location}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{getPropertyTypeIcon(property.type_id)}</span>
                          <span>{getPropertyTypeName(property.type_id)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">{property.location}</div>
                          <div className="text-muted-foreground">{property.address}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {property.capacity}
                          </div>
                          <div className="flex items-center gap-1">
                            <Bed className="h-4 w-4" />
                            {property.bedrooms}
                          </div>
                          <div className="flex items-center gap-1">
                            <Bath className="h-4 w-4" />
                            {property.bathrooms}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          <SaudiRiyal amount={property.full_price} variant="new" />
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {property.deposit_type === 'fixed' ? (
                            <SaudiRiyal amount={property.deposit_value} variant="new" />
                          ) : `${property.deposit_value}%`}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Badge variant={property.active ? "default" : "secondary"}>
                            {property.active ? t("active") : t("inactive")}
                          </Badge>
                          {property.featured && (
                            <Badge variant="outline" className="flex items-center gap-1">
                              <Star className="h-3 w-3" />
                              {t("featured")}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleViewProperty(property)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditProperty(property)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600"
                            onClick={() => setDeleteProperty(property)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="property-types" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("propertyTypes")}</CardTitle>
                <Button onClick={handleCreatePropertyType}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("createPropertyType")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {propertyTypes.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">{t("noPropertyTypes")}</h3>
                  <p className="text-muted-foreground mb-4">{t("addFirstPropertyType")}</p>
                  <Button onClick={handleCreatePropertyType}>
                    <Plus className="mr-2 h-4 w-4" />
                    {t("createPropertyType")}
                  </Button>
                </div>
              ) : typesLoading ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Card key={i} className="animate-pulse">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="w-8 h-8 bg-muted rounded"></div>
                          <div className="flex gap-1">
                            <div className="w-8 h-8 bg-muted rounded"></div>
                            <div className="w-8 h-8 bg-muted rounded"></div>
                            <div className="w-8 h-8 bg-muted rounded"></div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="h-6 bg-muted rounded mb-2"></div>
                        <div className="h-4 bg-muted rounded mb-2"></div>
                        <div className="h-6 bg-muted rounded w-20"></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {propertyTypes.map((type) => (
                    <Card key={type.id} className="group hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="text-2xl">{type.icon}</div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleViewPropertyType(type)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEditPropertyType(type)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-600"
                              onClick={() => setDeletePropertyType(type)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <h3 className="font-semibold">{type.name_en}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {type.description_en}
                        </p>
                        <div className="mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {Array.isArray(properties) ? properties.filter(p => p && p.type_id === type.id.toString()).length : 0} {t("properties")}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="media" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold tracking-tight">{t("mediaManagement")}</h2>
              <p className="text-muted-foreground">
                {t("uploadAndManagePropertyMedia")}
              </p>
            </div>
          </div>

          {/* Property Selection for Media Upload */}
          <Card>
            <CardHeader>
              <CardTitle>{t("selectPropertyForMedia")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <Select 
                  value={selectedPropertyForMedia} 
                  onValueChange={setSelectedPropertyForMedia}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("selectProperty")} />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(properties) && properties.map(property => (
                      <SelectItem key={property.id} value={property.id.toString()}>
                        <div className="flex items-center gap-2">
                          <span>{getPropertyTypeIcon(property.type_id?.toString() || "")}</span>
                          <span>{property.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedPropertyForMedia && (
                  <div className="mt-4">
                    <PropertyMediaManager 
                      propertyId={selectedPropertyForMedia}
                      onMediaUploaded={() => {
                        // Refresh property data if needed
                        refetchProperties();
                      }}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Property Form Modal */}
      <PropertyForm
        isOpen={showPropertyForm}
        onClose={handleClosePropertyForm}
        property={editingProperty}
        propertyTypes={propertyTypes}
        onSave={handleSaveProperty}
      />

      {/* Property View Modal */}
      <PropertyView
        isOpen={showPropertyView}
        onClose={handleClosePropertyView}
        property={viewingProperty}
        onEdit={handleEditProperty}
        onDelete={(property) => setDeleteProperty(property)}
      />

      {/* Property Type Form Modal */}
      <PropertyTypeForm
        isOpen={showPropertyTypeForm}
        onClose={handleClosePropertyTypeForm}
        propertyType={editingPropertyType}
        onSave={handleSavePropertyType}
      />

      {/* Property Type View Modal */}
      <PropertyTypeView
        isOpen={showPropertyTypeView}
        onClose={handleClosePropertyTypeView}
        propertyType={viewingPropertyType}
        onEdit={handleEditPropertyType}
        onDelete={(type) => setDeletePropertyType(type)}
        propertyCount={viewingPropertyType && Array.isArray(properties) ? properties.filter(p => p && p.type_id === viewingPropertyType.id.toString()).length : 0}
      />

      {/* Delete Property Type Confirmation */}
      <AlertDialog open={!!deletePropertyType} onOpenChange={() => setDeletePropertyType(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deletePropertyType")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirmDeletePropertyType")}
              {deletePropertyType && (
                <div className="mt-2 p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{deletePropertyType.icon}</span>
                    <div>
                      <div className="font-medium">{deletePropertyType.name_en}</div>
                      <div className="text-sm text-muted-foreground">{deletePropertyType.name_ar}</div>
                    </div>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletePropertyType && handleDeletePropertyType(deletePropertyType)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Property Confirmation */}
      <AlertDialog open={!!deleteProperty} onOpenChange={() => setDeleteProperty(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteProperty")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirmDeleteProperty")}
              {deleteProperty && (
                <div className="mt-2 p-3 bg-muted rounded-lg">
                  <div className="font-medium">{deleteProperty.name}</div>
                  <div className="text-sm text-muted-foreground">{deleteProperty.location}</div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteProperty && handleDeleteProperty(deleteProperty)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 